import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Activity } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { formatDistanceToNow, parseISO } from "date-fns";

interface RecentTask {
  id: string;
  title: string;
  status: string;
  updated_at: string;
  job_id: string | null;
  jobs: { name: string } | null;
}

const STATUS_VARIANT: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
  Open: "outline",
  "In Progress": "default",
  Done: "secondary",
  Blocked: "destructive",
};

export function HomeRecentActivity() {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState<RecentTask[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchRecent = async () => {
      try {
        const { data } = await supabase
          .from("tasks")
          .select("id, title, status, updated_at, job_id, jobs(name)")
          .is("deleted_at", null)
          .order("updated_at", { ascending: false })
          .limit(5);

        setTasks((data as unknown as RecentTask[]) || []);
      } catch (err) {
        console.error("Recent activity error:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchRecent();
  }, []);

  if (loading) {
    return <Skeleton className="h-48 rounded-lg mb-6" />;
  }

  if (tasks.length === 0) return null;

  return (
    <Card className="mb-6 border-border bg-card/80 backdrop-blur-sm">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <Activity className="h-4 w-4 text-muted-foreground" />
          Recent Activity
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="space-y-3">
          {tasks.map((task) => (
            <button
              key={task.id}
              onClick={() =>
                task.job_id ? navigate(`/job/${task.job_id}`) : navigate("/today-board")
              }
              className="w-full flex items-center justify-between gap-2 text-left hover:bg-muted/50 rounded-md px-2 py-1.5 transition-colors"
            >
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-foreground truncate">
                  {task.title}
                </p>
                {task.jobs?.name && (
                  <p className="text-xs text-muted-foreground truncate">
                    {task.jobs.name}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <Badge variant={STATUS_VARIANT[task.status] || "outline"} className="text-[10px] px-1.5 py-0">
                  {task.status}
                </Badge>
                <span className="text-[10px] text-muted-foreground whitespace-nowrap">
                  {formatDistanceToNow(parseISO(task.updated_at), { addSuffix: true })}
                </span>
              </div>
            </button>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
